import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { EmployeeAvatar } from 'modules/employees/components/EmployeeAvatar'
import HoverInfoCard from 'modules/project_management/components/HoverInfoCard'
import {
  HoursToPlanHoverFooter,
  HoursToPlanHoverHeader,
} from 'modules/project_management/components/Schedule/Body/ScheduleItem/HeaderRow/EmployeeCellContent/HoursToPlanHover'
import PlannedHoursHover from 'modules/project_management/components/Schedule/Body/ScheduleItem/HeaderRow/EmployeeCellContent/PlannedHoursHover'
import { formatDecimalHours } from 'modules/project_management/components/Schedule/Body/ScheduleItem/utils'
import HoverScheduleCard from 'modules/project_management/components/Schedule/HoverScheduleCard'
import { useScheduleVisibilityOptions } from 'modules/project_management/contexts/ScheduleVisibilyOptionsContext'

interface Props {
  employee: EmployeeCellContentType
  open: boolean
  isChevronHidden: boolean
}

export type EmployeeCellContentType = {
  id: number
  fullName: string
  avatar: string
  jobTitle: string
  estimatedWorkTimes: number
  plannedWorkTimes?: number
  daysOffTimes: number
}

const EmployeeCellContent = ({ employee, open, isChevronHidden = false }: Props) => {
  const { visibilityOptions } = useScheduleVisibilityOptions()
  const { showAvatar, showRole, showPlanningHours, showAgreementHours } = visibilityOptions
  const agreementHours = (employee.estimatedWorkTimes + employee.daysOffTimes) / 60
  const estimatedHours = employee.estimatedWorkTimes / 60
  const plannedHours = employee.plannedWorkTimes ? employee.plannedWorkTimes / 60 : 0
  const hoursOff = employee.daysOffTimes / 60

  return (
    <Box
      flexDirection="row"
      gap="s8"
      minWidth="none"
      padding="s8"
      height="s80"
      justifyContent="spaceBetween"
    >
      <Box flexDirection="row" gap="s8">
        {showAvatar && (
          <EmployeeAvatar
            variant="medium"
            employee={{
              id: employee.id,
              access: {
                id: employee.id,
                fullName: employee.fullName,
                avatar: employee.avatar,
              },
            }}
          />
        )}
        <Box overflowX="hidden" gap="s4" width="s140">
          <Text size="200" weight="medium" color="grey1000" ellipsis>
            {employee.fullName}
          </Text>
          {showRole && (
            <Text size="100" color="grey800" ellipsis>
              {employee.jobTitle}
            </Text>
          )}

          <Box flexDirection="row" alignItems="center" gap="s4">
            {(showPlanningHours || showAgreementHours) && (
              <Icon.Tiny icon="clock" color="grey600" />
            )}
            {showPlanningHours && (
              <HoverInfoCard content={<PlannedHoursHover plannedHours={plannedHours} />}>
                <Box flexDirection="row">
                  <Text size="100" dottedUnderline>
                    {formatDecimalHours(plannedHours)}
                    {i18n.t('project_management.planning.employee_info.hours_unit')}
                  </Text>
                </Box>
              </HoverInfoCard>
            )}
            {showPlanningHours && showAgreementHours && <>/</>}
            {showAgreementHours && (
              <HoverScheduleCard
                header={<HoursToPlanHoverHeader hoursToPlan={estimatedHours} />}
                footer={
                  <HoursToPlanHoverFooter agreementHours={agreementHours} hoursOff={hoursOff} />
                }
              >
                <Box flexDirection="row">
                  <Text size="100" dottedUnderline>
                    {formatDecimalHours(estimatedHours)}
                    {i18n.t('project_management.planning.employee_info.hours_unit')}
                  </Text>
                </Box>
              </HoverScheduleCard>
            )}
          </Box>
        </Box>
      </Box>
      <Box width="fit-content">
        {!isChevronHidden && (
          <Icon.Small icon={open ? 'chevron-up' : 'chevron-down'} color="grey800" />
        )}
      </Box>
    </Box>
  )
}

export default EmployeeCellContent
